import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SecurityContext,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, flatMap } from 'rxjs/operators';

import { IMapComponent } from '../IMapComponent';
import { IMarkerPoint, IPoint, MarkerColor, MarkerSize } from '../IPoint';
import {
    DivIcon,
    divIcon,
    LatLngBounds,
    map,
    Map,
    Marker,
    marker,
    point,
    Point,
    Polyline,
    polyline,
    PolylineOptions,
    tileLayer,
    TileLayer,
    control
} from "leaflet";
import { ISegment } from '../ISegment';
import { ILeafletConfiguration } from './ILeafletConfiguration';
import { MapProvider } from 'libraries/interfaces/MapProvider';
import { BLUE, GREEN, RED } from '../Colors';
import { ICenterRadiusBounds, IMapBounds, IMinimumBounds } from '../IMapBounds';
import { MapComputationService } from '../computation/map-computation.service';
import { NominatimResourceService } from './nominatim-resource-service';
import { IPlaceResult } from './IPlaceResult';
import { ILabels } from 'libraries/interfaces/ILabels';
import { Yii } from 'libraries/interfaces/Yii';
import { FormBuilder, FormGroup } from '@angular/forms';
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

const DEFAULT_CONFIGURATION: ILeafletConfiguration = {
    provider: MapProvider.OpenStreetMap,
    tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

const DEFAULT_CENTER: IPoint = {
    latitude: 46.8167,
    longitude: 1.7,
};

class Wording {
    public readonly search:string = Yii.t("wui-shared-js", "Rechercher un lieu");
}

@Component({
    selector: 'leaflet-map',
    template: `
        <div #container class="map"></div>
        <form-field *ngIf="activateSearch"
                    matAutocompleteOrigin
                    #placesField="matAutocompleteOrigin"
                    [options]="searchTextOptions"
                    [formGroup]="searchForm">
            <mat-form-field>
                <input #search
                       matInput
                       type="text"
                       formControlName='text'
                       [attr.placeholder]="labels.search"
                       [matAutocomplete]="places"
                       [matAutocompleteConnectedTo]="placesField">
                <mat-autocomplete #places="matAutocomplete"
                                  (optionSelected)="onSelectedPlace($event)"
                                  [autoActiveFirstOption]="true"
                                  [displayWith]="getPlaceName">
                    <mat-option *ngFor="let place of searchResults | async; trackBy: getPlaceId" [value]="place">
                        {{place.name}}
                    </mat-option>
                </mat-autocomplete>
            </mat-form-field>
        </form-field>
    `,
    styles: [`
        :host {
            display: block;
            position: relative;
        }

        div.map {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
        }

        form-field {
            position: absolute;
            top: 10px;
            left: 50px;
            z-index: 500;
            height: 40px;
        }

        mat-form-field {
            width: 30em;
        }
    `],
    styleUrls: ['./leafletMap.scss']
})
export class LeafletMapComponent implements OnInit, OnChanges, IMapComponent, OnDestroy, AfterViewInit {
    @Input()
    public defaultCenter: IPoint = DEFAULT_CENTER;

    @Input()
    public defaultBounds: IMinimumBounds | ICenterRadiusBounds;

    @Input()
    public points: IMarkerPoint[];

    @Input()
    public segments: ISegment[];

    @Input()
    public configuration: ILeafletConfiguration = DEFAULT_CONFIGURATION;

    @Input()
    public activateSearch: boolean;

    @Input()
    public searchText: string;

    @Input()
    public defaultZoom: number = 5;

    @Output()
    public readonly onMarkerClick: EventEmitter<IMarkerPoint> = new EventEmitter();

    @Output()
    public readonly onBoundsChanged: EventEmitter<IMapBounds> = new EventEmitter();

    @ViewChild('container')
    public container: ElementRef;

    public readonly labels: Wording = new Wording();

    public searchTextOptions: IFieldSpecs;
    public searchResults: Observable<IPlaceResult[]>;

    public searchForm: FormGroup;

    private map: Map;
    private tileLayer: TileLayer;
    private markerElements: Marker[];
    private lines: Polyline[];
    private isMouseInMap: boolean;
    private isCenterChanged: boolean;

    constructor(
        private readonly domSanitizer: DomSanitizer,
        private readonly mapComputationService: MapComputationService,
        private readonly nominatimResource: NominatimResourceService,
        private readonly formBuilder: FormBuilder,
        private readonly errorTracingService: ErrorTracingService,
    ) {
    }

    public ngOnInit(): void {

        this.searchForm = this.formBuilder.group({
            "text": this.formBuilder.control({name: this.searchText})
        });

        this.searchTextOptions = {
            formControlName: "text",
            formGroup: this.searchForm,
        };


        if (this.searchText && this.activateSearch) {
            this.nominatimResource.search(this.searchText)
                .pipe(
                    catchError((error: Error): Observable<IPlaceResult[]> => {
                        this.errorTracingService.captureException(error);
                        return of([]);
                    })
                )
                .subscribe({
                    next: (places: IPlaceResult[]): void => {
                        this.fitFirstPlace(places);
                    }
                });
        }

        this.searchResults = this.searchForm.get(["text"])
            .valueChanges
            .pipe(
                debounceTime(200),
                filter(Boolean),
                filter((value: unknown): boolean => typeof value === "string"),
                flatMap((value: string): Observable<IPlaceResult[]> => this.nominatimResource.search(value))
            );
    }

    public ngAfterViewInit(): void {
        this.map = map(this.container.nativeElement);

        this.map.addEventListener("mouseover", (): void => {
            this.isMouseInMap = true;
        });
        this.map.addEventListener("mouseout", (): void => {
            this.isMouseInMap = false;
        });
        this.map.addEventListener("moveend", (): void => {
            this.onMapMove();
        });
        this.map.addEventListener("zoomend", (): void => {
            this.onMapMove();
        });
        this.map.addEventListener("resize", (): void => {
            this.onMapMove();
        });

        if (!this.tileLayer) {
            this.tileLayer = createTileLayer(this.configuration);
        }

        this.tileLayer.addTo(this.map);
        //#30525 adding scale to maps
        control.scale({metric: true, imperial: true}).addTo(this.map);

        this.updateMarkers();
        this.updateSegments();


        setTimeout((): void => {
            this.invalidateSize();
            this.updateZoom();
        }, 10);
    }

    public ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map.removeEventListener("zoomend");
            this.map.removeEventListener("resize");
            this.map.removeEventListener("moveend");
        }

        this.map = null;
        this.lines = null;
        this.markerElements = null;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.configuration && changes.configuration.currentValue) {
            if (this.tileLayer) {
                this.tileLayer.remove();
            }

            this.tileLayer = createTileLayer(this.configuration);

            if (this.map) {
                this.tileLayer.addTo(this.map);
            }
        }

        if (changes.points && changes.points.currentValue && this.map) {
            this.updateMarkers();
            this.updateSegments();
            if (this.mapComputationService.haveChangedPoints(changes.points.previousValue, changes.points.currentValue)) {
                this.updateZoom();
            }
        }

    }

    public invalidateSize(): void {
        this.map && this.map.invalidateSize();
    }

    public getPlaceId(place: IPlaceResult): number {
        return place.id;
    }

    public onSelectedPlace(event: MatAutocompleteSelectedEvent): void {
        this.isCenterChanged = true;
        this.fitBounds(event.option.value.bounds);
        this.onMapMove();
        setTimeout((): void=>{
            this.isCenterChanged = false;
        }, 10);
    }

    public getPlaceName(place: IPlaceResult): string {
        return place.name;
    }

    private updateMarkers(): void {
        if (this.markerElements) {
            this.markerElements.forEach((element: Marker): void => {
                element.remove();
            });
        }

        this.markerElements = (this.points || []).map((markerPoint: IMarkerPoint): Marker => {
            const label: string = this.domSanitizer.sanitize(SecurityContext.NONE, markerPoint.label);
            const fontSize: number = markerPoint.label && markerPoint.label.length === 1 ? 14 : 12;
            let color: string;

            switch (markerPoint.color) {
                case MarkerColor.Red:
                    color = `#${RED}`;
                    break;
                case MarkerColor.Green:
                    color = `#${GREEN}`;
                    break;
                case MarkerColor.Blue:
                default:
                    color = `#${BLUE}`;
                    break;
            }

            let html: string;
            let iconSize: Point;
            let iconAnchor: Point;

            if (label) {
                // html = `<span class="fa-stack fa-lg">
                //         <span class="fa fa-map-marker fa-stack-2x" style="color:${color}"></span>
                //         <span class="fa-stack-1x fa-inverse" style='top:-5px; text-align:center'>
                //             <span style="background-color:${color};
                //                          border-radius:5px;
                //                          font-size:${fontSize}px;
                //                          display:inline-block;
                //                          line-height: normal;
                //                          min-width: 15px;">${label}</span>
                //         </span>
                //         </span>`;

                //non testé !!
                html = `<div>
<svg width="100%" height="100%" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" version="1.1" style="position: absolute; left:0; top:0;">
                            <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z"
                                    fill="${color}" stroke-width="3" />
                        </svg>
<span style='top:-5px; text-align:center'>
                            <span style="background-color:${color};
                                         border-radius:5px;
                                         font-size:${fontSize}px;
                                         display:inline-block;
                                         line-height: normal;
                                         min-width: 15px;">${label}</span>
                        </span>
</div>`;

                iconSize = point(32, 33);
                iconAnchor = point(16, 30);
            } else {
                // html = ` <span class="fa fa-map-marker ${markerPoint.size === MarkerSize.Large ? "fa-4x" : "fa-3x"}" style="color:${color}"></span>`;
                html = ` <svg width="100%" height="100%" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" version="1.1">
                            <path d="M172.268 501.67C26.97 291.031 0 269.413 0 192 0 85.961 85.961 0 192 0s192 85.961 192 192c0 77.413-26.97 99.031-172.268 309.67-9.535 13.774-29.93 13.773-39.464 0z"
                                    fill="${color}" stroke-width="3" />
                        </svg>`;

                if (markerPoint.size === MarkerSize.Large) {
                    iconSize = point(27, 48);
                    iconAnchor = point(13, 45);
                } else {
                    iconSize = point(21, 36);
                    iconAnchor = point(10, 33);
                }
            }

            const icon: DivIcon = divIcon({
                html,
                className: 'marker',
                iconSize,
                iconAnchor,
            });

            return marker([markerPoint.latitude, markerPoint.longitude], {icon})
                .addTo(this.map)
                .on("click", (): void => {
                    this.onMarkerClick.emit(markerPoint);
                });
        });
    }

    private updateSegments(): void {
        if (this.lines) {
            this.lines.forEach((line: Polyline): void => {
                line.remove();
            });
        }

        const options: PolylineOptions = {
            stroke: true,
            color: `#${BLUE}`,
            weight: 3,
            opacity: 1,
        };

        this.lines = (this.segments || []).map((segment: ISegment): Polyline =>
            polyline([
                [segment.from.latitude, segment.from.longitude],
                [segment.to.latitude, segment.to.longitude],
            ], options).addTo(this.map)
        );
    }

    private updateZoom(): void {
        if (this.points && this.points.length) {
            if (this.points.length === 1) {
                this.map.setView([this.points[0].latitude, this.points[0].longitude], this.defaultZoom);
            } else {
                const allPoints: Array<[number, number]> = this.points
                    .map((pointItem: IMarkerPoint): [number, number] => [pointItem.latitude, pointItem.longitude]);

                const bounds: LatLngBounds = new LatLngBounds(allPoints);
                this.map.fitBounds(bounds.pad(0.05));
            }
        } else if (this.defaultBounds && (this.defaultBounds as IMinimumBounds).ne) {
            this.fitBounds(this.defaultBounds as IMinimumBounds);
        } else if (this.defaultBounds && (this.defaultBounds as ICenterRadiusBounds).radiusInMeters) {
            const centerRadiusBounds: ICenterRadiusBounds = this.defaultBounds as ICenterRadiusBounds;
            const east: IPoint = this.mapComputationService.computeOffset(
                centerRadiusBounds.center,
                90,
                centerRadiusBounds.radiusInMeters
            );
            const west: IPoint = this.mapComputationService.computeOffset(
                centerRadiusBounds.center,
                270,
                centerRadiusBounds.radiusInMeters
            );

            const bounds: LatLngBounds = new LatLngBounds([
                [east.latitude, east.longitude],
                [west.latitude, west.longitude],
            ]);

            this.map.fitBounds(bounds);
        } else {
            const defaultCenter: IPoint = this.defaultCenter || DEFAULT_CENTER;
            this.map.setView([defaultCenter.latitude, defaultCenter.longitude], this.defaultZoom);
        }

    }

    private fitBounds(pointsBounds: IMinimumBounds): void {
        const bounds: LatLngBounds = new LatLngBounds([
            [pointsBounds.ne.latitude, pointsBounds.ne.longitude],
            [pointsBounds.sw.latitude, pointsBounds.sw.longitude],
        ]);

        this.map.fitBounds(bounds);
    }

    private onMapMove(): void {
        const bounds: LatLngBounds = this.map.getBounds();

        const ne: IPoint = {latitude: bounds.getNorthEast().lat, longitude: bounds.getNorthEast().lng};
        const nw: IPoint = {latitude: bounds.getNorthEast().lat, longitude: bounds.getSouthWest().lng};
        const sw: IPoint = {latitude: bounds.getSouthWest().lat, longitude: bounds.getSouthWest().lng};
        const se: IPoint = {latitude: bounds.getSouthWest().lat, longitude: bounds.getNorthEast().lng};

        const mapBounds: IMapBounds = {
            center: {latitude: bounds.getCenter().lat, longitude: bounds.getCenter().lng},
            ne,
            nw,
            sw,
            se,
            distanceInMeters: {
                ew: this.mapComputationService.computeDistance(ne, nw),
                ns: this.mapComputationService.computeDistance(ne, se)
            }
        };
        if (this.isMouseInMap || this.isCenterChanged) {
            this.onBoundsChanged.emit(mapBounds);
        }
    }

    private fitFirstPlace(places: IPlaceResult[]): void {
        if (places && places.length) {
            this.fitBounds(places[0].bounds);
        }
    }
}

function createTileLayer(inputConfiguration: ILeafletConfiguration): TileLayer {
    const configuration: ILeafletConfiguration = inputConfiguration || DEFAULT_CONFIGURATION;
    const tileUrl: string = configuration.tileUrl || DEFAULT_CONFIGURATION.tileUrl;

    const attribution: string = configuration.attribution === undefined ? DEFAULT_CONFIGURATION.attribution : configuration.attribution;

    return tileLayer(tileUrl, {
        attribution,
    });
}
